import { OverlayLeaveButton, ResponsiveOverlay } from '@/components/responsive-overlay';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueries } from '@tanstack/react-query';

import { useRefreshAudience } from '@/api/manage-hooks';
import type { AudienceSyncStatus } from '@/api/manage-hooks';
import { audienceRevisionSyncStatus } from '@/api/resources';
import { CADENCE } from '@/api/resource';
import { Icons } from '@/components/primitives';
import { FailureNotice } from '@/components/failure-notice';
import {
  audienceLegNeedsAttention,
  audienceLegState,
  type AudienceLegOutcome,
} from '@/lib/audience-refresh-view';
import { reportActionError } from '@/lib/report-action-error';

export interface AudienceSyncGateItem {
  id: string;
  label: string;
  revision: number;
  target: 'meta' | 'ttd';
}

interface AudienceSyncGateProps {
  items: AudienceSyncGateItem[];
  onSynced: () => void | Promise<void>;
  onReturnToDraft: () => void;
  /** When set (campaign context), offers arming the backend to perform the
   *  launch automatically once everything reports ready. */
  onLaunchWhenReady?: () => void | Promise<void>;
}

type SyncSnapshot = AudienceSyncStatus & {
  meta_status?: string | null;
  ttd_status?: string | null;
};

function humanizeStatus(value: string | null | undefined): string {
  if (!value) return 'Waiting';
  return value
    .split('_')
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
}

function targetProgress(
  snapshot: SyncSnapshot | undefined,
  target: 'meta' | 'ttd',
): number | null {
  if (!snapshot) return null;
  const fraction = target === 'meta'
    ? snapshot.meta?.step_progress?.fraction ?? snapshot.meta?.progress
    : snapshot.ttd?.step_progress?.fraction;
  if (typeof fraction !== 'number' || !Number.isFinite(fraction)) return null;
  return Math.max(0, Math.min(1, fraction));
}

function targetStatus(
  snapshot: SyncSnapshot | undefined,
  target: 'meta' | 'ttd',
): string | null | undefined {
  return target === 'meta' ? snapshot?.meta_status : snapshot?.ttd_status;
}

/**
 * What this leg's status says, in the one vocabulary `lib/audience-refresh-view`
 * owns. The gate used to answer "keep polling?" and "finished?" from two
 * inline lists, and a leg the server reported as `null` — no sync-state row for
 * that (audience, revision, target) — satisfied neither: polling never stopped,
 * completion never fired, and this modal is `dismissible={false}`.
 */
function legOutcome(
  snapshot: SyncSnapshot | undefined,
  target: 'meta' | 'ttd',
): AudienceLegOutcome {
  return audienceLegState(snapshot, target);
}

function TargetState({
  label,
  status,
  outcome,
  progress,
}: {
  label: string;
  status: string | null | undefined;
  outcome: AudienceLegOutcome;
  progress: number | null;
}) {
  const failed = audienceLegNeedsAttention(outcome);
  const synced = outcome === 'synced';
  // A leg with no row has no status word of its own, and "Waiting" is what made
  // the wedge look healthy.
  const wording = status ? humanizeStatus(status) : outcome === 'unreported'
    ? 'Not reported'
    : 'Waiting';
  return (
    <div style={{ minWidth: 118, flex: 1 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 8,
          fontSize: 10.5,
          lineHeight: 1.4,
        }}
      >
        <b style={{ color: 'var(--ink)' }}>{label}</b>
        <span style={{ color: failed ? 'var(--bad)' : synced ? 'var(--good)' : 'var(--ink-2)' }}>
          {wording}
        </span>
      </div>
      <div
        aria-hidden="true"
        style={{
          height: 3,
          marginTop: 5,
          overflow: 'hidden',
          borderRadius: 99,
          background: 'var(--line)',
        }}
      >
        <div
          style={{
            width: `${synced ? 100 : Math.round((progress ?? 0.08) * 100)}%`,
            height: '100%',
            borderRadius: 99,
            background: failed ? 'var(--bad)' : synced ? 'var(--good)' : 'var(--accent)',
            transition: 'width .25s ease',
          }}
        />
      </div>
    </div>
  );
}

/**
 * Browser-side blocking gate required by the campaign workflow. It observes
 * revision-scoped audience materialization only; it never creates provider
 * campaign, ad-group, or creative objects.
 */
export function AudienceSyncGate({
  items,
  onSynced,
  onReturnToDraft,
  onLaunchWhenReady,
}: AudienceSyncGateProps) {
  const refreshAudience = useRefreshAudience();
  const [actionError, setActionError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const onSyncedRef = useRef(onSynced);
  const completionSent = useRef(false);
  onSyncedRef.current = onSynced;

  const signature = JSON.stringify(items);
  const stableItems = useMemo(
    () => items.map((item) => ({ ...item })),
    // Equivalent freshly allocated props must not restart every observer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature],
  );

  const statusQueries = useQueries({
    queries: stableItems.map((item) => audienceRevisionSyncStatus(
      { id: item.id, revision: item.revision },
      {
        // The stop-set and the completion set below are now the same partition:
        // the poll runs while — and only while — something is moving the leg.
        cadence: (snapshot) => (
          legOutcome(snapshot, item.target) === 'working' ? CADENCE.fast : false
        ),
      },
    )),
  });

  const snapshots = useMemo<Record<string, SyncSnapshot>>(() => {
    const next: Record<string, SyncSnapshot> = {};
    statusQueries.forEach((query, index) => {
      const item = stableItems[index];
      if (item && query.data) next[item.id] = query.data;
    });
    return next;
  }, [stableItems, statusQueries]);

  const queryError = statusQueries.find((query) => query.error)?.error;
  const transportError = actionError ?? (queryError instanceof Error
    ? queryError.message
    : queryError ? String(queryError) : null);

  const completionKey = stableItems
    .map((item) => `${item.id}:${item.revision}:${item.target}`)
    .join('|');

  useEffect(() => {
    completionSent.current = false;
  }, [completionKey]);

  useEffect(() => {
    if (stableItems.length === 0) {
      if (!completionSent.current) {
        completionSent.current = true;
        void onSyncedRef.current();
      }
      return;
    }
    const allSynced = stableItems.every((item) => (
      legOutcome(snapshots[item.id], item.target) === 'synced'
    ));
    if (allSynced && !completionSent.current) {
      completionSent.current = true;
      void onSyncedRef.current();
    }
  }, [snapshots, stableItems]);

  // A failed leg and a leg the server never reported are the same thing to an
  // operator: nothing will move it, and the way forward is the same re-drive.
  const attentionIds = [...new Set(stableItems
    .filter((item) => audienceLegNeedsAttention(legOutcome(snapshots[item.id], item.target)))
    .map((item) => item.id))];
  const needsAttention = attentionIds.length > 0;
  const hasFailedLeg = stableItems.some((item) => (
    legOutcome(snapshots[item.id], item.target) === 'failed'
  ));

  const retry = async () => {
    setRefreshing(true);
    setActionError(null);
    try {
      const ids = attentionIds.length > 0
        ? attentionIds
        : [...new Set(stableItems.map((item) => item.id))];
      await Promise.all(ids.map((id) => refreshAudience.mutateAsync(id)));
      await Promise.all(statusQueries
        .filter((_, index) => ids.includes(stableItems[index]?.id ?? ''))
        .map((query) => query.refetch()));
    } catch (error) {
      setActionError(reportActionError(error, { operation: 'campaign.audience_sync_retry' }));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <ResponsiveOverlay label="Publishing progress" kind="progress" className="progress-host" dismissible={false} onClose={onReturnToDraft}>
      <div
        className="launch-dialog"
        style={{ maxHeight: '90vh' }}
        aria-labelledby="audience-sync-title"
        aria-describedby="audience-sync-description"
      >
        <div className="launch-dialog-hd">
          <h3 id="audience-sync-title">
            {needsAttention ? 'Audience sync needs attention' : 'Preparing audiences'}
          </h3>
          <div className="sub" id="audience-sync-description">
            Each pinned audience revision is being materialized independently
            for this campaign&apos;s destination. The campaign stays a draft
            until every required audience is ready.
          </div>
        </div>
        <div
          className="launch-dialog-body"
          aria-live="polite"
          style={{ overflowY: 'auto' }}
        >
          <div className="lbl">
            {needsAttention
              ? (hasFailedLeg ? 'Terminal failure' : 'No status reported')
              : 'Live platform status'}
          </div>
          <div className="launch-dialog-list">
            {stableItems.map((item, index) => {
              const snapshot = snapshots[item.id];
              const status = targetStatus(snapshot, item.target);
              const outcome = legOutcome(snapshot, item.target);
              return (
                <div
                  className="item"
                  key={`${item.id}:${item.revision}:${item.target}:${index}`}
                  style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}
                >
                  <span
                    className="item-ico"
                    style={{
                      color: audienceLegNeedsAttention(outcome)
                        ? 'var(--bad)'
                        : outcome === 'synced'
                          ? 'var(--good)'
                          : 'var(--ink-2)',
                    }}
                  >
                    {outcome === 'synced' ? Icons.check : Icons.audiences}
                  </span>
                  <div style={{ flex: '1 1 340px', minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, marginBottom: 8 }}>
                      <b>Audience {index + 1}:</b> {item.label}{' '}
                      <span className="muted">· revision {item.revision}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                      <TargetState
                        label={item.target === 'meta' ? 'Meta' : 'TTD'}
                        status={status}
                        outcome={outcome}
                        progress={targetProgress(snapshot, item.target)}
                      />
                    </div>
                    {audienceLegNeedsAttention(outcome) && snapshot?.last_error && (
                      <div style={{ marginTop: 8 }}>
                        <FailureNotice
                          dense
                          announce={false}
                          failure={snapshot.failure}
                          fallback={snapshot.last_error}
                        />
                      </div>
                    )}
                    {outcome === 'unreported' && (
                      // There is no `last_error` to show: the row the error
                      // would have lived on is the thing that is missing.
                      <div style={{ color: 'var(--bad)', fontSize: 10.5, marginTop: 8 }}>
                        {item.target === 'meta' ? 'Meta' : 'TTD'} reported no sync state for
                        revision {item.revision}. Nothing is working on it — retry to re-drive
                        this audience, or continue in the background and check again later.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {transportError && (
            <div style={{ color: 'var(--bad)', fontSize: 11.5, marginTop: 10 }}>
              Status check failed: {transportError}
            </div>
          )}
        </div>
        <div className="launch-dialog-foot">
          <button className="btn" type="button" onClick={onReturnToDraft}>
            {needsAttention || transportError ? 'Return to draft' : 'Continue in background'}
          </button>
          {onLaunchWhenReady && !needsAttention && (
            <OverlayLeaveButton
              className="btn btn-primary"
              onLeave={() => {
                void onLaunchWhenReady();
              }}
            >
              Launch automatically when ready
            </OverlayLeaveButton>
          )}
          {(needsAttention || transportError) && (
            <button
              className="btn btn-primary"
              type="button"
              disabled={refreshing}
              onClick={() => { void retry(); }}
            >
              {Icons.refresh} {refreshing ? 'Retrying…' : 'Retry audience sync'}
            </button>
          )}
          {!needsAttention && !transportError && (
            <span className="muted" style={{ fontSize: 11.5 }}>
              Checking again automatically…
            </span>
          )}
        </div>
      </div>
    </ResponsiveOverlay>
  );
}
